import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface Slot {
  startTime: string;
  endTime: string;
}

export interface GetSlotsResponse {
  success: boolean;
  data?: {
    slots: Slot[];
    clientName: string;
  };
  error?: string;
  code?: string;
  // Informações quando o token já foi usado
  schedulingInfo?: {
    clientName: string;
    status: string;
    scheduledAt: string | null;
    eventId: string | null;
  };
}

export interface CreateBookingResponse {
  success: boolean;
  message?: string;
  data?: {
    eventId: string;
    startTime: string;
    endTime: string;
    htmlLink: string;
  };
  error?: string;
}

/**
 * Hook customizado para gerenciar agendamentos
 */
export function useScheduling(token: string | null) {
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  // Buscar slots disponíveis
  const {
    data: slotsData,
    isLoading: isLoadingSlots,
    error: slotsError,
    refetch: refetchSlots,
  } = useQuery<GetSlotsResponse>({
    queryKey: ['slots', token],
    queryFn: async () => {
      if (!token) {
        throw new Error('Token não fornecido');
      }

      const response = await fetch(`${API_BASE_URL}/schedule/slots?token=${token}`);
      const data = await response.json();

      if (!response.ok) {
        // Se o token já foi usado, retornar os dados mesmo com erro
        if (data.code === 'TOKEN_ALREADY_USED' && data.data) {
          return {
            success: false,
            error: data.error,
            code: data.code,
            schedulingInfo: data.data,
          };
        }
        throw new Error(data.error || 'Erro ao buscar horários');
      }

      return data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutos
    retry: (failureCount, error: Error) => {
      // Não fazer retry se for TOKEN_ALREADY_USED
      if (error?.message?.includes('já foi processado')) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Criar booking
  const createBookingMutation = useMutation<
    CreateBookingResponse,
    Error,
    { token: string; startTime: string }
  >({
    mutationFn: async ({ token: bookingToken, startTime }) => {
      const response = await fetch(`${API_BASE_URL}/schedule/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: bookingToken,
          startTime,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao criar agendamento');
      }

      return response.json();
    },
  });

  const createBooking = async () => {
    if (!token || !selectedSlot) {
      throw new Error('Token ou slot não selecionado');
    }

    return createBookingMutation.mutateAsync({
      token,
      startTime: selectedSlot.startTime,
    });
  };

  return {
    // Dados dos slots
    slots: slotsData?.data?.slots || [],
    clientName: slotsData?.data?.clientName || slotsData?.schedulingInfo?.clientName || '',
    isLoadingSlots,
    slotsError:
      slotsError?.message ||
      (slotsData?.code !== 'TOKEN_ALREADY_USED' ? slotsData?.error : undefined),

    // Informações de agendamento já processado
    alreadyScheduled: slotsData?.code === 'TOKEN_ALREADY_USED',
    schedulingInfo: slotsData?.schedulingInfo,

    // Slot selecionado
    selectedSlot,
    setSelectedSlot,

    // Criar booking
    createBooking,
    isCreatingBooking: createBookingMutation.isPending,
    bookingError: createBookingMutation.error?.message,
    bookingSuccess: createBookingMutation.isSuccess,
    bookingData: createBookingMutation.data,

    // Utilidades
    refetchSlots,
    resetBooking: createBookingMutation.reset,
  };
}
